import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import MyceliumCanvas from "@/components/MyceliumCanvas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [ctaEmail, setCtaEmail] = useState("");
  const [heroSuccess, setHeroSuccess] = useState(false);
  const [ctaSuccess, setCtaSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const revealRefs = useRef([]);

  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    revealRefs.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  const addRevealRef = (el) => {
    if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el);
  };

  async function joinWaitlist(emailVal, setSuccess) {
    if (!emailVal || !emailVal.includes("@")) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, { email: emailVal, source: "landing" });
      setSuccess(true);
    } catch {
      setSuccess(true); // still show success (might be duplicate)
    }
    setSubmitting(false);
  }

  // Register SW
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
  }, []);

  return (
    <div style={{ background: "var(--deep)", minHeight: "100vh" }}>
      <MyceliumCanvas />

      <div className="landing-wrapper">
        {/* Nav */}
        <nav className="landing-nav" data-testid="landing-nav">
          <a href="/" className="nav-logo">
            <div className="nav-dot" />
            myhypha.ai
          </a>
          <div className="nav-links">
            <a href="#story" className="nav-link">The story</a>
            <a href="#builds" className="nav-link">What it builds</a>
            <a href="#roadmap" className="nav-link">Roadmap</a>
            <button
              className="nav-link"
              style={{ background: "none", border: "none", cursor: "pointer" }}
              onClick={() => navigate("/app")}
              data-testid="nav-try-app-btn"
            >
              Try the app
            </button>
          </div>
        </nav>

        {/* Hero */}
        <section className="landing-section landing-hero" data-testid="landing-hero">
          <p className="hero-eyebrow fade-rise-delay-1">Launching 2026 &middot; Human-first &middot; Model-agnostic</p>
          <h1 className="hero-headline fade-rise-delay-2">
            Your human network, <em>growing daily.</em>
          </h1>
          <p className="hero-sub fade-rise-delay-3">
            As AI advances, the scarcest resource isn't intelligence — it's proof of human contribution.
            Hypha is where you build yours, one day at a time.
          </p>

          {heroSuccess ? (
            <p className="success-msg fade-rise-delay-4" data-testid="hero-success-msg">
              You're on the list. We'll be in touch.
            </p>
          ) : (
            <div className="hero-form fade-rise-delay-4">
              <input
                className="hero-input"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") joinWaitlist(email, setHeroSuccess); }}
                data-testid="hero-email-input"
              />
              <button
                className="hero-btn"
                onClick={() => joinWaitlist(email, setHeroSuccess)}
                disabled={submitting}
                data-testid="hero-waitlist-btn"
              >
                Join the waitlist
              </button>
            </div>
          )}
          <p className="hero-footnote fade-rise-delay-5">
            No spam. Early access only. Works alongside Claude, GPT, Gemini — whatever ships next.
          </p>
        </section>

        <hr className="landing-divider" />

        {/* Pull Quote */}
        <div className="pull-quote reveal-el" ref={addRevealRef} id="story" data-testid="founding-quote">
          <blockquote>
            "A single hypha is invisible. Millions of them, woven underground, form the network
            that feeds entire forests — and survives every storm above."
          </blockquote>
          <cite>The founding insight</cite>
        </div>

        <hr className="landing-divider" />

        {/* Question */}
        <section className="landing-section question-section reveal-el" ref={addRevealRef} data-testid="question-section">
          <p className="section-eyebrow">The only question that matters</p>
          <div className="question-card">
            <p className="question-text">"What did you contribute today that no AI could replicate?"</p>
            <p className="question-sub">Five minutes. Every morning. For the rest of your life.</p>
          </div>
          <div className="entry-examples" style={{ marginTop: 32 }}>
            <div className="entry-example">"I talked my colleague off a ledge before the board presentation."</div>
            <div className="entry-example">"I made my daughter laugh so hard she cried — for the third time this week."</div>
            <div className="entry-example">"I read the room and said nothing. The silence saved the deal."</div>
            <div className="entry-example">"I forgave someone who didn't ask for it."</div>
          </div>
        </section>

        <hr className="landing-divider" />

        {/* What it builds */}
        <section className="landing-section reveal-el" ref={addRevealRef} id="builds" data-testid="builds-section">
          <p className="section-eyebrow">What Hypha builds for you</p>
          <h2 className="section-headline">
            Not a journal. <em>A proof of life.</em>
          </h2>
          <p className="section-body">
            Over months and years, your daily answers become the most complete record of your
            human contribution ever assembled. Not your resume. Not your LinkedIn. The real thing.
          </p>
          <div className="builds-grid">
            {[
              { num: "01", title: "Your meaning portfolio", body: "An evolving map of your uniquely human capabilities — the moments, skills, and contributions that define who you are beyond any job title." },
              { num: "02", title: "Your irreplaceability record", body: "As automation displaces tasks, your Hypha shows what remains — and what grows. A living answer to \"what's my value in an AI world?\"" },
              { num: "03", title: "Your human network", body: "Mycelium-style routing connects you to people who need your specific capabilities. Trust-based, not algorithm-ranked. Each connection strengthens everyone." },
              { num: "04", title: "Your transition readiness", body: "A personal navigator from today's economy to the post-AGI era. Financial, identity, and purpose readiness — updated as the world changes around you." },
            ].map((b) => (
              <div key={b.num} className="build-cell">
                <div className="build-num">{b.num}</div>
                <div className="build-title">{b.title}</div>
                <div className="build-body">{b.body}</div>
              </div>
            ))}
          </div>
        </section>

        <hr className="landing-divider" />

        {/* Roadmap */}
        <section className="landing-section reveal-el" ref={addRevealRef} id="roadmap" data-testid="roadmap-section">
          <p className="section-eyebrow">The 20-year arc</p>
          <h2 className="section-headline">
            We're building roots. <em>Like everything that lasts.</em>
          </h2>
          <p className="section-body">
            Bamboo builds its root system for five years before erupting ninety feet in sixty days.
            We're in the root phase. What we plant now makes the exponential growth inevitable.
          </p>
          <div className="timeline">
            {[
              { date: "2025 - 2027", title: "Root phase — build underground", body: "The daily check-in habit. The meaning portfolio. A founding community of 10,000 people building the first human contribution network. No noise above ground yet." },
              { date: "2027 - 2031", title: "First shoots — the displacement wave", body: "First wave of white-collar AI displacement. Millions searching for identity and meaning. Hypha surfaces with years of trust data and network effects already built." },
              { date: "2031 - 2037", title: "Canopy ascent — the AGI era", body: "AGI arrives in some form. UBI begins. Hypha becomes Moorings — the human layer beneath every AI system, every government program, every economy rebuilding itself." },
              { date: "2037 - 2044", title: "Forest canopy — the human network is the economy", body: "Work is optional. The meaning portfolio is the most valuable record a human can hold. What you started building in 2025 is now the infrastructure of civilisation." },
            ].map((t, i) => (
              <div key={i} className="timeline-item">
                <div className="timeline-date">{t.date}</div>
                <div>
                  <div className="timeline-title">{t.title}</div>
                  <div className="timeline-body">{t.body}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <hr className="landing-divider" />

        {/* CTA */}
        <section className="cta-section reveal-el" ref={addRevealRef} data-testid="cta-section">
          <h2 className="cta-headline">"Check your moorings."</h2>
          <p className="cta-sub">Every day. For the rest of your life.</p>
          {ctaSuccess ? (
            <p className="success-msg" data-testid="cta-success-msg">
              You're on the list. We'll be in touch.
            </p>
          ) : (
            <div className="cta-form">
              <input
                className="hero-input"
                type="email"
                placeholder="your@email.com"
                value={ctaEmail}
                onChange={(e) => setCtaEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") joinWaitlist(ctaEmail, setCtaSuccess); }}
                data-testid="cta-email-input"
              />
              <button
                className="hero-btn"
                onClick={() => joinWaitlist(ctaEmail, setCtaSuccess)}
                disabled={submitting}
                data-testid="cta-waitlist-btn"
              >
                Join the waitlist
              </button>
            </div>
          )}
        </section>

        {/* Footer */}
        <footer className="landing-footer" data-testid="landing-footer">
          <span className="footer-logo">myhypha.ai</span>
          <span className="footer-mono">
            <button
              style={{ background: "none", border: "none", color: "inherit", font: "inherit", cursor: "pointer" }}
              onClick={() => navigate("/app")}
              data-testid="footer-try-app-btn"
            >
              Try the daily check-in
            </button>
          </span>
        </footer>
      </div>
    </div>
  );
}
